"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"
import { InputDescription } from "../../../components/InputRow/InputDescription"
import { ProcessedEntry } from "@/types/import"
import { MirrorImportAcknowledge } from "./MirrorImportAcknowledge"
import { MirrorImportHandler } from "./MirrorImportHandler"
import { BackButton } from "./BackButton"
import useMirrorImportFlow from "./hooks/useMirrorImportFlow"

interface MirrorImportProps {
  fullWidthButton?: boolean
  defaultEntries?: ProcessedEntry[]
  prefilledWallet?: `0x${string}`
  onBack?: () => void
  onComplete?: () => void
}

/**
 * Component for importing posts from Mirror.
 * Handles the multi-stage import flow.
 * Uses custom hooks for state management and logic.
 */
export function MirrorImport(props: MirrorImportProps) {
  const { defaultEntries = [], prefilledWallet, onBack, onComplete } = props

  // Use custom hook for Mirror import flow state management
  const {
    stage,
    wallet,
    setWallet,
    isLoading,
    error,
    entries,
    notes,
    importOptions,
    setImportOptions,
    handleWalletSubmit,
    handleContinueToImport,
    goBack,
  } = useMirrorImportFlow(defaultEntries, prefilledWallet)

  // Render the correct stage
  if (stage === "ENTER_WALLET") {
    return (
      <div className="space-y-4">
        <form className="space-y-4" onSubmit={handleWalletSubmit}>
          <div className="space-y-2">
            <InputDescription>Wallet address</InputDescription>
            <Input
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
              placeholder="0x123456789..."
              className="max-w-[400px]"
            />
            <p className="text-sm text-muted-foreground">
              This should be the same address that’s stored on your Arweave
              entries in the contributor field.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-3 !mt-6">
            {onBack && <BackButton onClick={onBack} />}
            <Button
              type="submit"
              disabled={!wallet || isLoading}
              className={props.fullWidthButton ? "w-full" : ""}
            >
              {isLoading ? "Loading..." : "Import posts"}
            </Button>
          </div>
        </form>
      </div>
    )
  } else if (stage === "ACKNOWLEDGE" && entries.length > 0) {
    return (
      <MirrorImportAcknowledge
        entries={entries}
        importOptions={importOptions}
        setImportOptions={setImportOptions}
        onBack={goBack}
        onContinue={handleContinueToImport}
      />
    )
  } else if (stage === "IMPORTING" && notes.length > 0) {
    return (
      <MirrorImportHandler
        notes={notes}
        importOptions={importOptions}
        onBack={goBack}
        onComplete={onComplete}
      />
    )
  }

  return null
}
